'use client';

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Amenity, ListingType, ReadOnlyListing } from 'hotdesk_typescript';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import { MyListing } from '@/hotdesk-ui/services/listing.service';
import { LanguageCode } from '@/hotdesk-ui/constants/langulage.constant';
import { getListingData, getWorkspaceData, setActiveButton as setActiveButtonDispatcher, setListings as listingSetter, setWorkspaces as workspaceSetter, setTotalWorkspace as totalWorkspaceSetter, setTotallisting as totallistingSettor, setWorkspacePage, setListingPage, setFilters, getApiFilters } from '@/hotdesk-ui/store/slices/listing.slice';
import { setGeoLocation, toggelLocationPopup } from '@/hotdesk-ui/store/slices/geo-location.slice';
import { setCurrentSearch } from '@/hotdesk-ui/store/slices/search.slice';
import { getDefaultLocation } from '@/hotdesk-ui/helpers/location.helper';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { fetchRemoteConfig, firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { ListingService } from '@/hotdesk-ui/services/listing.service';
import { mergeRegusAmenitiesIntoFilterList } from '@/hotdesk-ui/utils/amenities';
import { getListingProviderCode } from '@/hotdesk-ui/utils/provider';
import { FilterProvider } from '../filters/FiltersContext';
import { buildExploreFetchParams, getUserSubscriptionIds, mergeSubscriptionIntoExploreFilters } from '@/hotdesk-ui/utils/subscriptionUtils';
import ExploreFilterChips from './ExploreFilterChips';
import ExploreToolbar from './ExploreToolbar';
import ExploreGrid from './ExploreGrid';
import ExploreSplit from './ExploreSplit';
import ExploreMap from './ExploreMap';
import ExploreFiltersModal from './ExploreFiltersModal';
import { LongTermQuoteProvider } from '@/hotdesk-ui/components/long-term-quote/LongTermQuoteContext';
import LongTermBookingModal from '@/hotdesk-ui/components/long-term-quote/LongTermBookingModal';
import LongTermLeasePromoBanner, { LongTermLeasePromoBannerSkeleton } from '@/hotdesk-ui/components/long-term-quote/LongTermLeasePromoBanner';
import ExploreMembershipOfferBanner from '@/hotdesk-ui/components/explore-v4/ExploreMembershipOfferBanner';
import CompareToggleButton from '@/hotdesk-ui/components/compare/explore/CompareToggleButton';
import CompareFloatingBar from '@/hotdesk-ui/components/compare/explore/CompareFloatingBar';
import { useExploreCompare } from '@/hotdesk-ui/hooks/compare/useExploreCompare';
import { CompareExitMethod, CompareType } from '@/hotdesk-ui/enum/compare';
import { storeSet } from '@/hotdesk-ui/helpers/utils';
import { applyGeoToSearchParams, countryCodeToDisplayName, filterIwgQuoteOnlyListings, filtersFromSearchParams, geoAreaKey, geoAreaScope, geoCentreCircle, geoFromSearchParams, isAreaWideGeo, isLongTermFrequency, LONG_TERM_IWG_QUOTE_FREQUENCY, normalizeLongTermOfficesFrequency, replaceExploreUrlWithGeo, shouldPersistGeoInUrl } from '@/hotdesk-ui/helpers/explore-location-params';
export interface ExploreClientProps {
  activeButton: Carousel;
  totalWorkspace: number;
  totallisting: number;
  workspaces: MyListing[];
  listings: MyListing[];
  allAminities: Amenity[];
  listingRes: ListingType[];
  languageCode: LanguageCode;
  envFilters: string;
  type: 'country' | 'city' | 'workspace';
  country?: string;
  city?: string;
  ipCountry?: string;
}

/** `view` tracking value for compare events. */
const COMPARE_VIEW_NAME: Partial<Record<Carousel, string>> = {
  [Carousel.listing]: 'grid',
  [Carousel.split]: 'split',
  [Carousel.fullMap]: 'map'
};
const PAGE_SIZE = 20;
/** Secondary long-term IWG/Regus day/hour quote fetch size. */
const IWG_QUOTE_FETCH_LIMIT = 100;
/** Avoid zero-result fetches when the map is zoomed in very far. */
const MIN_MAP_VIEWPORT_RADIUS_KM = 5;

/**
 * Call alongside anything that restarts the results at page 1.
 *
 * A list grown by "Show more" collapses back to a single page, but the browser
 * keeps a scroll position that was only valid for the long one — which lands
 * the viewport down in the footer, looking like the page navigated there.
 *
 * Instant rather than smooth: the list is being torn down and replaced during
 * the scroll, so animating through it just shows the teardown.
 */
const scrollResultsToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto'
  });
};

// Keeps the shareable URL in sync when a subscription filter is manually active.
const ExploreFilterSubscriptionInit: FC = () => {
  const searchParams = useSearchParams();
  const reduxFilters = useAppSelector((s: RootState) => s.listing.filters);
  const geoLocation = useAppSelector((s: RootState) => s.geoLocation.geoLocationZipData);
  const subscriptionIdIn = reduxFilters.userSubscriptionIdIn || '';
  useEffect(() => {
    if (!subscriptionIdIn) return;
    const urlSubIn = searchParams.get('userSubscriptionIdIn') || '';
    if (urlSubIn !== subscriptionIdIn) {
      replaceExploreUrlWithGeo(geoLocation, {
        userSubscriptionIdIn: subscriptionIdIn
      });
    }
  }, [subscriptionIdIn, searchParams, geoLocation]);
  return null;
};
const MULTI_SELECT_FILTER_KEYS = new Set(['typeIn', 'amenitiesIdIn', 'paymentTypeIn']);
const countActiveFilterValue = (key: string, value: string): number => {
  if (key === 'quickBooking') {
    return value === 'true' ? 1 : 0;
  }
  if (key === 'userSubscriptionIdIn') {
    return value.split(',').filter(Boolean).length > 0 ? 1 : 0;
  }
  if (key === 'filterCapOrQtyGte') {
    return Number(value) > 1 ? 1 : 0;
  }
  if (MULTI_SELECT_FILTER_KEYS.has(key)) {
    return value.split(',').filter(Boolean).length;
  }
  if (key === 'priceGte' && (value === '0' || value === '')) {
    return 0;
  }
  return 1;
};
const ExploreClient: FC<ExploreClientProps> = ({
  activeButton,
  totalWorkspace,
  totallisting,
  workspaces,
  listings,
  allAminities,
  listingRes,
  languageCode,
  type,
  country,
  city
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const autocompleteInput = useRef<HTMLInputElement>(null);
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  /** Ensures we only seed Redux from URL location once per mount. */
  const urlLocationSeededRef = useRef(false);
  const fillSearchInput = useCallback((address: string) => {
    const input = autocompleteInput.current;
    if (!input) return;
    input.value = address;
    input.dispatchEvent(new Event('input', {
      bubbles: true
    }));
  }, []);
  const initialView = (() => {
    const param = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('active') : null;
    const viewValues = [Carousel.listing, Carousel.split, Carousel.fullMap] as string[];
    return param && viewValues.includes(param) ? param as Carousel : Carousel.split;
  })();
  const [viewMode, setViewMode] = useState<Carousel>(initialView);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [sourceOfLocationData, setSourceOfLocationData] = useState('');
  const geoLocation = useAppSelector((s: RootState) => s.geoLocation.geoLocationZipData);

  // A country-wide geo (e.g. "Egypt") can't be fetched by lat/lng — a radius
  // around the country centroid usually contains nothing — so it goes out as
  // a country filter instead. Match on the ISO code (`countryCode` →
  // `city__country__code__iexact`), never on a name: `Intl.DisplayNames`
  // returns CLDR's current label, and where that has moved on from what the
  // DB stores the `__iexact` match silently found nothing. TR is the one
  // that bit us — CLDR says "Türkiye", the country table says "Turkey" — and
  // it's a class, not a one-off: CI (Côte d'Ivoire / Ivory Coast), MM, BA,
  // CD, CV, HK and PS all disagree the same way. Egypt only ever worked
  // because the two spellings happen to agree.
  //
  // Passing the geo through hands it to the slice's own country-code branch,
  // which is what the map canvas fetch has been using all along.
  const buildGeoFetchParams = useCallback((geo: GeoCodeResult | null | undefined): {
    country?: string;
    lastSearch?: GeoCodeResult;
  } => {
    // No ISO code (shouldn't happen — Google gives the country's
    // short_name) leaves only the label Google supplied.
    if (geo?.types?.includes('country') && !geo.country_sort && geo.country) {
      return {
        country: geo.country
      };
    }
    return geo ? {
      lastSearch: geo
    } : {};
  }, []);
  const handleChangeView = (mode: Carousel) => {
    setViewMode(mode);
    replaceExploreUrlWithGeo(geoLocation, {
      active: mode
    });
  };
  const handleToggleExpandMap = (expand: boolean) => {
    if (expand) {
      handleChangeView(Carousel.fullMap);
      return;
    }
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
    handleChangeView(isMobile ? Carousel.listing : Carousel.split);
  };
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = () => {
      if (mq.matches) setViewMode(v => v === Carousel.split ? Carousel.listing : v);
    };
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const geoLocationSource = geoLocation?.source;
  const listingTypesItems = useAppSelector((s: RootState) => s.listingTypes.items);
  const listingTypesLoading = useAppSelector((s: RootState) => s.listingTypes.loading);
  // The Redux copy is rehydrated from localStorage at an unpredictable point
  // in hydration, so the server (no copy) and a returning visitor's browser
  // drew different chips — a hydration mismatch. Until hydrated, render the
  // list the server fetched for this page; it's the same API data.
  const serverListingTypes = useMemo(() => (listingRes || []).map(t => ({
    id: t.id as number,
    type: t.type as string,
    icon: (t as {
      url?: string;
    }).url || `/assets/icons/icon+${t.id}.svg`
  })), [listingRes]);
  const chipListingTypes = hydrated && listingTypesItems.length ? listingTypesItems : serverListingTypes;
  const chipListingTypesLoading = hydrated ? listingTypesLoading : false;
  const activeButtonSelected = useAppSelector((s: RootState) => s.listing.activeButton);
  const workspacesSelector = useAppSelector((s: RootState) => s.listing.workspaces);
  const listingsSelector = useAppSelector((s: RootState) => s.listing.listings);
  const totalWorkspaceSel = useAppSelector((s: RootState) => s.listing.totalWorkspace);
  const totalListingSel = useAppSelector((s: RootState) => s.listing.totallisting);
  const workspacePage = useAppSelector((s: RootState) => s.listing.workspacePage);
  const listingPage = useAppSelector((s: RootState) => s.listing.listingPage);
  const workspaceBtnLoading = useAppSelector((s: RootState) => s.listing.workspaceBtnLoading);
  const listingBtnLoading = useAppSelector((s: RootState) => s.listing.listingBtnLoading);
  const isListingLoading = useAppSelector((s: RootState) => s.listing.isListingLoading);
  const isWorkspaceLoading = useAppSelector((s: RootState) => s.listing.isWorkspaceLoading);
  const reduxFilters = useAppSelector((s: RootState) => s.listing.filters);
  const amenitiesMap = useAppSelector((s: RootState) => s.amenities.items);
  const user = useAppSelector((s: RootState) => s.auth.user);
  const userSubscriptionIds = useMemo(() => getUserSubscriptionIds(user), [user]);

  /** Shareable filter query signature — changes on soft-nav (e.g. Long-term). */
  const urlFilterSignature = useMemo(() => {
    const fromUrl = filtersFromSearchParams(searchParams);
    const normalizedFrequency = normalizeLongTermOfficesFrequency(fromUrl.frequency);
    if (normalizedFrequency) {
      fromUrl.frequency = normalizedFrequency;
    }
    return JSON.stringify(fromUrl);
  }, [searchParams]);
  const appliedUrlFilterSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    dispatch(listingSetter(listings));
    dispatch(workspaceSetter(workspaces));
    dispatch(totalWorkspaceSetter(totalWorkspace));
    dispatch(totallistingSettor(totallisting));
    dispatch(setActiveButtonDispatcher(activeButton));
    dispatch(setListingPage(1));
    dispatch(setWorkspacePage(1));
    const fromUrl = filtersFromSearchParams(searchParams);
    const normalizedFrequency = normalizeLongTermOfficesFrequency(fromUrl.frequency);
    if (fromUrl.frequency && normalizedFrequency && normalizedFrequency !== fromUrl.frequency) {
      fromUrl.frequency = normalizedFrequency;
      replaceExploreUrlWithGeo(geoFromSearchParams(searchParams) || geoLocation, {
        frequency: normalizedFrequency
      });
    }
    const merged = mergeSubscriptionIntoExploreFilters(fromUrl, reduxFilters, userSubscriptionIds);
    dispatch(setFilters(merged));
    appliedUrlFilterSignatureRef.current = urlFilterSignature;

    // A subscription id that the current user doesn't own (e.g. a logged-out
    // user landing on `?userSubscriptionIdIn=5851`) is dropped by the merge —
    // strip it from the URL too so it doesn't linger or get re-applied.
    if (fromUrl.userSubscriptionIdIn !== undefined && merged.userSubscriptionIdIn !== fromUrl.userSubscriptionIdIn) {
      replaceExploreUrlWithGeo(geoFromSearchParams(searchParams) || geoLocation, {
        userSubscriptionIdIn: merged.userSubscriptionIdIn || null
      });
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, workspaces, totalWorkspace, totallisting, activeButton]);

  // Soft-nav while already on explore (navbar/footer "Long-term offices")
  // updates the URL via router.push but does not remount this client tree —
  // re-apply filters + refetch so month/annual actually take effect.
  useEffect(() => {
    if (!hydrated) return;
    if (appliedUrlFilterSignatureRef.current === urlFilterSignature) {
      return;
    }
    appliedUrlFilterSignatureRef.current = urlFilterSignature;
    const fromUrl = filtersFromSearchParams(searchParams);
    const normalizedFrequency = normalizeLongTermOfficesFrequency(fromUrl.frequency);
    if (fromUrl.frequency && normalizedFrequency && normalizedFrequency !== fromUrl.frequency) {
      fromUrl.frequency = normalizedFrequency;
      replaceExploreUrlWithGeo(geoFromSearchParams(searchParams) || geoLocation, {
        frequency: normalizedFrequency
      });
    }
    const merged = mergeSubscriptionIntoExploreFilters(fromUrl, reduxFilters, userSubscriptionIds);
    dispatch(setFilters(merged));
    dispatch(setListingPage(1));
    dispatch(setWorkspacePage(1));
    scrollResultsToTop();
    const fetchParams = buildExploreFetchParams({
      page: 1,
      type,
      country,
      city,
      geoLocation
    });
    dispatch(getListingData({
      ...fetchParams,
      filters: merged
    }));
    dispatch(getWorkspaceData({
      ...fetchParams,
      filters: merged
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilterSignature, hydrated]);

  // Place picked from the custom suggestions dropdown.
  const handlePlaceSelect = useCallback((placeData: GeoCodeResult) => {
    placeData.source = 'autocomplete';
    dispatch(setCurrentSearch(placeData));
    dispatch(setGeoLocation(placeData));
    dispatch(setWorkspacePage(1));
    dispatch(setListingPage(1));
    scrollResultsToTop();
    // Persist location in the URL so the link can be copied.
    replaceExploreUrlWithGeo(placeData);
    const fetchParams = buildGeoFetchParams(placeData);
    dispatch(getListingData({
      ...fetchParams,
      page: 1
    }));
    dispatch(getWorkspaceData({
      ...fetchParams,
      page: 1
    }));
  }, [dispatch, buildGeoFetchParams]);

  // Prefill search with the page context (country / city). Skip the address
  // Reflect the resolved location in the search box — including the user's
  // own detected geolocation (arriving from the landing "Nearby" search),
  // which now shows its reverse-geocoded address here just like the landing.
  useEffect(() => {
    if (!autocompleteInput.current) return;
    if (type === 'city' && country && city) {
      fillSearchInput(`${country}, ${city}`);
    } else if (type === 'country' && country) {
      fillSearchInput(country);
    } else if (geoLocation?.formatted_address) {
      fillSearchInput(geoLocation.formatted_address);
    }
  }, [type, country, city, geoLocation?.formatted_address, fillSearchInput]);

  // On /workspaces mount, check whether browser location is enabled.
  // `sourceOfLocationData` is used by other flows to know the active source.
  useEffect(() => {
    if (type !== 'workspace') return;

    // Read location from the URL on load (shared / copied link). If present,
    // seed it once and let it drive the page instead of resolving location.
    if (!urlLocationSeededRef.current) {
      urlLocationSeededRef.current = true;
      const urlGeo = geoFromSearchParams(new URLSearchParams(window.location.search), languageCode);
      if (urlGeo) {
        const sameCoords = urlGeo.lat != null && urlGeo.log != null && geoLocation?.lat != null && geoLocation?.log != null && Math.abs(geoLocation.lat - urlGeo.lat) < 1e-4 && Math.abs(geoLocation.log - urlGeo.log) < 1e-4;
        // Only skip re-seeding a country (or state) link when Redux
        // already holds that same *area-wide* search (e.g. navigated
        // from landing). If Redux holds a specific point (real GPS/IP
        // location), a `countryCode` link must still switch to the
        // country-wide search, mirroring /workspaces?country=….
        const urlArea = geoAreaKey(geoAreaScope(urlGeo));
        const sameArea = !!urlArea && urlArea === geoAreaKey(geoAreaScope(geoLocation));
        if (sameCoords || sameArea) return;
        dispatch(setGeoLocation(urlGeo));
        dispatch(setCurrentSearch(urlGeo));
        if (!isAreaWideGeo(urlGeo) && urlGeo.lat != null && urlGeo.log != null) {
          getDefaultLocation(urlGeo.lat, urlGeo.log, (result: GeoCodeResult) => {
            dispatch(setGeoLocation({
              ...result,
              lat: urlGeo.lat,
              log: urlGeo.log
            }));
            dispatch(setCurrentSearch(result));
          });
        }
        return;
      }
    } else if (geoFromSearchParams(new URLSearchParams(window.location.search))) {
      // URL already drives location; don't override it.
      return;
    }
    const shouldResolveLocation = !geoLocation || geoLocationSource === 'remote-default';
    const applyResult = (result: GeoCodeResult, coords?: {
      lat: number;
      log: number;
    }) => {
      const payload = coords ? {
        ...result,
        lat: coords.lat,
        log: coords.log,
        source: 'user-geolocation' as const
      } : {
        ...result,
        source: 'remote-default' as const
      };
      dispatch(setGeoLocation(payload));
      dispatch(setCurrentSearch(payload));
    };
    const loadDefault = async () => {
      storeSet('sourceOfLocationData', 'remote-default');
      setSourceOfLocationData('remote-default');
      if (!shouldResolveLocation) return;
      // Guard against an infinite loop: once any location exists (even a
      // previous 'remote-default'), don't reverse-geocode the default
      // again — doing so dispatches setGeoLocation → re-runs this effect →
      // geocodes again. This surfaced on Arc, where the geolocation
      // permission is 'prompt' (not 'granted'), so we always took this
      // default branch and looped GeocodeService.Search endlessly.
      if (geoLocation) return;
      const defaultLocation = (await fetchRemoteConfig(RemoteConfigKeys.DEFAULT_LOCATION, 'object')) as {
        [key: string]: number;
      };
      if (!defaultLocation?.latitude || !defaultLocation?.longitude) return;
      getDefaultLocation(defaultLocation.latitude, defaultLocation.longitude, (result: GeoCodeResult) => applyResult(result));
    };
    if (!('geolocation' in navigator) || !('permissions' in navigator)) {
      loadDefault();
      return;
    }
    navigator.permissions.query({
      name: 'geolocation' as PermissionName
    }).then(p => {
      if (p.state === 'granted') {
        storeSet('sourceOfLocationData', 'user-geolocation');
        setSourceOfLocationData('user-geolocation');
        if (!shouldResolveLocation) return;
        navigator.geolocation.getCurrentPosition(({
          coords
        }) => {
          getDefaultLocation(coords.latitude, coords.longitude, (result: GeoCodeResult) => applyResult(result, {
            lat: coords.latitude,
            log: coords.longitude
          }));
        }, () => loadDefault());
      } else {
        loadDefault();
      }
    }).catch(() => loadDefault());
  }, [dispatch, type, geoLocation, geoLocationSource, languageCode]);

  // Store the current location in the URL so the link can be shared/copied.
  // Only fill it in when the URL has no location yet — never rewrite a
  // location that's already in the URL (e.g. a shared countryCode / lat-lon
  // link). Rewriting a shared link would (a) change its meaning and (b) in
  // Next's App Router keep re-triggering the server render (infinite POST).
  // Explicit user searches update the URL through their own handlers.
  useEffect(() => {
    if (type !== 'workspace' || !geoLocation) return;
    if (!shouldPersistGeoInUrl(geoLocation)) return;
    const current = new URLSearchParams(window.location.search);
    const hasUrlLocation = current.get('countryCode') || current.get('lat') || current.get('lon') || current.get('log') || current.get('lng');
    if (hasUrlLocation) return;
    replaceExploreUrlWithGeo(geoLocation);
  }, [geoLocation, type]);
  const activeFilterCount = useMemo(() => {
    const countable = ['typeIn', 'frequency', 'priceGte', 'priceLte', 'amenitiesIdIn', 'paymentTypeIn', 'filterCapOrQtyGte', 'quickBooking', 'ratingGte', 'userSubscriptionIdIn'];
    return countable.reduce((acc, key) => {
      const v = reduxFilters[key];
      if (!v) return acc;
      return acc + countActiveFilterValue(key, String(v));
    }, 0);
  }, [reduxFilters]);
  const selectedTypeIn = reduxFilters.typeIn || '';

  // Pills — same routing semantics as LandingClient.addFilters
  const setActiveAndUrl = (next: Carousel, extra: Record<string, string> = {}) => {
    replaceExploreUrlWithGeo(geoLocation, {
      active: next,
      ...extra
    });
    dispatch(setActiveButtonDispatcher(next));
  };
  const handleSelectWorkspaces = () => {
    const next = {
      ...reduxFilters
    };
    delete next.typeIn;
    dispatch(setFilters(next));
    setActiveAndUrl(Carousel.workspace, {
      typeIn: ''
    });
    dispatch(setWorkspacePage(1));
    scrollResultsToTop();
    const params: any = {
      page: 1
    };
    if (type === 'country' && country) params.country = country;else if (type === 'city' && country && city) {
      params.country = country;
      params.city = city;
    } else Object.assign(params, buildGeoFetchParams(geoLocation));
    dispatch(getWorkspaceData(params));
    firebaseAnalytics('explore_pill_clicked', {
      type: 'workspaces'
    });
  };
  const handleSelectType = (typeId: number) => {
    const current = selectedTypeIn ? selectedTypeIn.split(',').filter(Boolean) : [];
    const id = String(typeId);
    const toggled = current.includes(id) ? current.filter(v => v !== id) : [...current, id];
    if (toggled.length === 0) {
      handleSelectWorkspaces();
      return;
    }
    const typeInStr = toggled.join(',');
    const next = {
      ...reduxFilters,
      typeIn: typeInStr
    };
    dispatch(setFilters(next));
    setActiveAndUrl(Carousel.listing, {
      typeIn: typeInStr
    });
    dispatch(setListingPage(1));
    scrollResultsToTop();
    const params: any = {
      page: 1
    };
    if (type === 'country' && country) params.country = country;else if (type === 'city' && country && city) {
      params.country = country;
      params.city = city;
    } else Object.assign(params, buildGeoFetchParams(geoLocation));
    dispatch(getListingData(params));
    firebaseAnalytics('explore_pill_clicked', {
      typeIn: toggled
    });
  };

  // Instant booking (`quickBooking`) is on the chip row and in the filters
  // modal. It forces listing mode (only listings can be instant-booked) —
  // see hasListingModeFilters.
  const instantBookingActive = reduxFilters.quickBooking === 'true';
  const handleToggleInstantBooking = () => {
    const enable = !instantBookingActive;
    const next = {
      ...reduxFilters
    };
    if (enable) next.quickBooking = 'true';else delete next.quickBooking;
    dispatch(setFilters(next));
    const nextActive = next.typeIn || next.frequency || next.quickBooking ? Carousel.listing : Carousel.workspace;
    setActiveAndUrl(nextActive, {
      quickBooking: enable ? 'true' : ''
    });
    dispatch(setListingPage(1));
    dispatch(setWorkspacePage(1));
    scrollResultsToTop();
    const params: any = {
      page: 1
    };
    if (type === 'country' && country) params.country = country;else if (type === 'city' && country && city) {
      params.country = country;
      params.city = city;
    } else Object.assign(params, buildGeoFetchParams(geoLocation));
    dispatch(getListingData(params));
    dispatch(getWorkspaceData(params));
    if (enable) firebaseAnalytics('instant_conf_filter_applied');
  };

  // Search submit
  const handleSearch = () => {
    firebaseAnalytics('search_clicked');
    // If there's a valid geo location, the autocomplete 'place_changed' already
    // refreshed. Otherwise trigger a refetch on current redux filters.
    if (geoLocation) {
      const fetchParams = buildGeoFetchParams(geoLocation);
      dispatch(getListingData({
        ...fetchParams,
        page: 1
      }));
      dispatch(getWorkspaceData({
        ...fetchParams,
        page: 1
      }));
    }
  };
  const handleClearSearch = useCallback(() => {
    firebaseAnalytics('search_cleared');
  }, []);
  const searchDisplayKey = useMemo(() => {
    const geo = geoLocation?.formatted_address ?? '';
    if (type === 'city' && country && city) {
      return `${country}, ${city}|${geo}`;
    }
    if (type === 'country' && country) {
      return `${country}|${geo}`;
    }
    return geo;
  }, [type, country, city, geoLocation?.formatted_address]);

  // Load more
  // Page whichever list is on screen. A footer link like
  // `/workspaces?typeIn=1` leaves the active button on workspace while the
  // grid shows listings, so the raw button would grow the hidden list.
  const handleLoadMore = () => {
    const isWs = isWorkspaceMode;
    const nextPage = (isWs ? workspacePage : listingPage) + 1;
    if (isWs) dispatch(setWorkspacePage(nextPage));else dispatch(setListingPage(nextPage));
    let params: any = {
      page: nextPage,
      limit: PAGE_SIZE
    };
    if (type === 'country' && country) {
      params.country = country;
    } else if (type === 'city' && country && city) {
      params.country = country;
      params.city = city;
    } else if (type === 'workspace' && geoLocation) {
      Object.assign(params, buildGeoFetchParams(geoLocation));
    }
    if (isWs) dispatch(getWorkspaceData(params));else dispatch(getListingData(params));
  };
  const locationClick = () => {
    if ('geolocation' in navigator) {
      navigator.permissions.query({
        name: 'geolocation'
      }).then(p => {
        if (p.state === 'granted') {
          navigator.geolocation.getCurrentPosition(({
            coords
          }) => {
            getDefaultLocation(coords.latitude, coords.longitude, (result: GeoCodeResult) => {
              result.source = 'user-geolocation';
              fillSearchInput(result.formatted_address);
              dispatch(setGeoLocation(result));
              dispatch(setCurrentSearch(result));
              // The fetches below restart at page 1, so the
              // counters have to come back with them —
              // "Show more" derives the next page from these,
              // and left stale it would ask for the page
              // after the old position and skip everything
              // between.
              dispatch(setWorkspacePage(1));
              dispatch(setListingPage(1));
              replaceExploreUrlWithGeo(result);
              // Later than the other callers on purpose: the
              // permission prompt and the fix are async, so
              // scrolling on click would move the page before
              // we know the location resolves at all.
              scrollResultsToTop();
              dispatch(getListingData({
                lastSearch: result,
                page: 1
              }));
              dispatch(getWorkspaceData({
                lastSearch: result,
                page: 1
              }));
            });
          });
        } else {
          dispatch(toggelLocationPopup(true));
        }
      });
    }
  };

  // Active data source: workspace pill uses workspaces, type pill uses listings.
  // Time / instant filters always show listings (same as type pills).
  //
  // `typeIn` counts whichever way it arrives. The pills set it alongside
  // `active=listing` (see handleSelectType), but a link from outside carries
  // only the filter — the footer's "Desks" is `/workspaces?typeIn=1` — and
  // that fell back to the remote-config default of workspace mode. The page
  // then filtered by type while counting and captioning workspaces: "171
  // workspaces in Cairo Governorate" for what the same filter calls spaces
  // when picked from the pills.
  const hasListingModeFilters = useMemo(() => {
    const typeIn = searchParams.get('typeIn') || reduxFilters.typeIn;
    const frequency = searchParams.get('frequency') || reduxFilters.frequency;
    const quickBooking = searchParams.get('quickBooking') ?? reduxFilters.quickBooking;
    return !!(typeIn || frequency || quickBooking === 'true');
  }, [searchParams, reduxFilters.typeIn, reduxFilters.frequency, reduxFilters.quickBooking]);

  // Until the mount effect copies the SSR props into Redux, read the props
  // directly — otherwise the server HTML (what crawlers index) renders an
  // empty grid, and a soft-nav briefly shows the previous page's cards.
  const mode = hydrated ? activeButtonSelected ?? activeButton : activeButton;
  const isWorkspaceMode = mode === Carousel.workspace && !hasListingModeFilters;
  const rawItems = (hydrated ? isWorkspaceMode ? workspacesSelector : listingsSelector : isWorkspaceMode ? workspaces : listings) as ReadOnlyListing[];
  const longTermFrequency = searchParams.get('frequency') || reduxFilters.frequency || null;
  const isLongTermExplore = isLongTermFrequency(longTermFrequency);

  // Long-term Offices: primary list is month/annual only. Separately fetch
  // day/hour inventory, keep IWG/Regus without a month rate, and append those
  // as Request Quote cards (non-Regus day/hour never appear).
  const [iwgQuoteListings, setIwgQuoteListings] = useState<ReadOnlyListing[]>([]);
  // API `count` of the quote fetch. Only IWG_QUOTE_FETCH_LIMIT rows come
  // back, so past that the heading uses this total instead of the page.
  const [iwgQuoteApiCount, setIwgQuoteApiCount] = useState(0);
  // Raw rows fetched so far (the next page's offset) — quote-only filtering
  // drops some, so this is not iwgQuoteListings.length.
  const [iwgQuoteFetched, setIwgQuoteFetched] = useState(0);
  const [iwgQuoteLoadingMore, setIwgQuoteLoadingMore] = useState(false);
  // Search the quote pages belong to; a load-more that resolves after the
  // search changed is dropped.
  const iwgQuoteKeyRef = useRef<string | null>(null);
  const radius = useAppSelector((s: RootState) => s.listing.radius);
  const defaultLanguage = useAppSelector((s: RootState) => s.listing.defaultLanguage);
  // Key of the in-flight / last-needed IWG quote fetch. Compared against
  // `iwgQuoteSettledKey` so pending is true on the same render the search
  // changes — before the effect runs — and the empty quote form never flashes.
  const iwgQuoteNeeded = isLongTermExplore && !isWorkspaceMode;
  const iwgQuoteRequestKey = useMemo(() => {
    if (!iwgQuoteNeeded) return null;
    return JSON.stringify({
      type,
      country,
      city,
      lat: geoLocation?.lat ?? null,
      log: geoLocation?.log ?? null,
      countrySort: geoLocation?.country_sort ?? null,
      regionName: geoLocation?.region_name ?? null,
      geoCountry: geoLocation?.country ?? null,
      geoTypes: geoLocation?.types ?? null,
      radius,
      filters: reduxFilters
    });
  }, [iwgQuoteNeeded, type, country, city, geoLocation?.lat, geoLocation?.log, geoLocation?.country_sort, geoLocation?.region_name, geoLocation?.country, geoLocation?.types, radius, reduxFilters]);
  const [iwgQuoteSettledKey, setIwgQuoteSettledKey] = useState<string | null>(null);
  const awaitingIwgQuotes = iwgQuoteRequestKey !== null && iwgQuoteSettledKey !== iwgQuoteRequestKey;

  // Fetches one page of the long-term IWG quote search (day/hour inventory
  // for the current location + filters). Resolves to undefined when the
  // search has no usable location.
  const fetchIwgQuotePage = useCallback(async (offset: number) => {
    const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
    const service = new ListingService(apiConfig);
    const baseFilters = {
      ...getApiFilters(reduxFilters),
      frequency: LONG_TERM_IWG_QUOTE_FREQUENCY
    };
    let listingRes;
    if (type === 'city' && country && city) {
      listingRes = await service.getallListings(service.getListingsDataParams(Carousel.listing, IWG_QUOTE_FETCH_LIMIT, offset, null, baseFilters, country, city));
    } else if (type === 'country' && country) {
      listingRes = await service.getallListings(service.getListingsDataParams(Carousel.listing, IWG_QUOTE_FETCH_LIMIT, offset, null, baseFilters, country));
    } else {
      const geoParams = buildGeoFetchParams(geoLocation);
      if (geoParams.country) {
        listingRes = await service.getallListings(service.getListingsDataParams(Carousel.listing, IWG_QUOTE_FETCH_LIMIT, offset, null, baseFilters, geoParams.country));
      } else if (geoAreaScope(geoParams.lastSearch)) {
        const scope = geoAreaScope(geoParams.lastSearch)!;
        listingRes = await service.getallListings(service.getListingsDataParams(Carousel.listing, IWG_QUOTE_FETCH_LIMIT, offset, geoCentreCircle(geoParams.lastSearch, radius), baseFilters, undefined, undefined, undefined, scope.countryCode, scope.region));
      } else if (geoParams.lastSearch?.lat != null && geoParams.lastSearch?.log != null) {
        listingRes = await service.getallListings(service.getListingsDataParams(Carousel.listing, IWG_QUOTE_FETCH_LIMIT, offset, {
          lat: geoParams.lastSearch.lat as number,
          lng: geoParams.lastSearch.log as number,
          radius
        }, baseFilters));
      } else {
        return undefined;
      }
    }
    return listingRes;
  }, [type, country, city, geoLocation, radius, defaultLanguage, reduxFilters, buildGeoFetchParams]);
  const toIwgQuoteOnly = (listingRes?: {
    results?: unknown[];
  }) => filterIwgQuoteOnlyListings((listingRes?.results || []) as ReadOnlyListing[], listing => getListingProviderCode(listing) === 'iwg');
  useEffect(() => {
    if (iwgQuoteRequestKey === null) {
      setIwgQuoteListings([]);
      setIwgQuoteSettledKey(null);
      setIwgQuoteApiCount(0);
      setIwgQuoteFetched(0);
      return;
    }
    let cancelled = false;
    iwgQuoteKeyRef.current = iwgQuoteRequestKey;
    setIwgQuoteListings([]);
    setIwgQuoteApiCount(0);
    setIwgQuoteFetched(0);
    setIwgQuoteLoadingMore(false);
    const run = async () => {
      try {
        const listingRes = await fetchIwgQuotePage(0);
        if (cancelled) return;
        setIwgQuoteListings(toIwgQuoteOnly(listingRes));
        setIwgQuoteApiCount(listingRes?.count ?? 0);
        setIwgQuoteFetched(listingRes?.results?.length ?? 0);
        setIwgQuoteSettledKey(iwgQuoteRequestKey);
      } catch (err) {
        console.error('Long-term IWG quote fetch failed', err);
        if (!cancelled) {
          setIwgQuoteListings([]);
          setIwgQuoteSettledKey(iwgQuoteRequestKey);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iwgQuoteRequestKey, fetchIwgQuotePage]);
  const iwgQuotesAllLoaded = iwgQuoteFetched >= iwgQuoteApiCount;

  // Next page of Request Quote cards, once the primary list is exhausted.
  const loadMoreIwgQuotes = async () => {
    if (iwgQuoteLoadingMore || iwgQuotesAllLoaded) return;
    const key = iwgQuoteRequestKey;
    setIwgQuoteLoadingMore(true);
    try {
      const listingRes = await fetchIwgQuotePage(iwgQuoteFetched);
      if (iwgQuoteKeyRef.current !== key) return;
      const next = toIwgQuoteOnly(listingRes);
      setIwgQuoteListings(prev => {
        const seen = new Set(prev.map(item => item.id));
        return [...prev, ...next.filter(item => !seen.has(item.id))];
      });
      const rows = listingRes?.results?.length ?? 0;
      // An empty page means the API ran out early — stop paging.
      setIwgQuoteFetched(prev => rows > 0 ? prev + rows : Math.max(prev, iwgQuoteApiCount));
      if (listingRes?.count != null) {
        setIwgQuoteApiCount(listingRes.count);
      }
    } catch (err) {
      console.error('Long-term IWG quote load more failed', err);
    } finally {
      if (iwgQuoteKeyRef.current === key) {
        setIwgQuoteLoadingMore(false);
      }
    }
  };

  // The server already narrows results to the selected subscription via the
  // `userSubscriptionIdIn` query param. ListingService then unique-counts
  // workspaces in distinct mode so the heading / "Show Results" match the
  // cards. Do not re-filter on `metaData.applicableSubscriptions` — that
  // tag isn't populated in workspace mode and used to blank the grid.
  const items = useMemo(() => {
    if (!isLongTermExplore || awaitingIwgQuotes || iwgQuoteListings.length === 0) {
      return rawItems;
    }
    const primaryIds = new Set(rawItems.map(item => item.id).filter(id => id != null));
    const extras = iwgQuoteListings.filter(item => item.id != null && !primaryIds.has(item.id));
    return extras.length > 0 ? [...rawItems, ...extras] : rawItems;
  }, [rawItems, iwgQuoteListings, isLongTermExplore, awaitingIwgQuotes]);
  const iwgQuoteExtraCount = useMemo(() => {
    if (!isLongTermExplore || awaitingIwgQuotes || iwgQuoteListings.length === 0) {
      return 0;
    }
    if (iwgQuoteApiCount > IWG_QUOTE_FETCH_LIMIT) {
      return iwgQuoteApiCount;
    }
    const primaryIds = new Set(rawItems.map(item => item.id).filter(id => id != null));
    return iwgQuoteListings.filter(item => item.id != null && !primaryIds.has(item.id)).length;
  }, [rawItems, iwgQuoteListings, iwgQuoteApiCount, isLongTermExplore, awaitingIwgQuotes]);

  // Curated Hotdesk amenity filters + any amenity ids used by Regus/IWG in
  // the current explore results (same ids that already render on cards).
  const filterAmenities = useMemo(() => mergeRegusAmenitiesIntoFilterList(allAminities, [...(workspacesSelector as ReadOnlyListing[]), ...(listingsSelector as ReadOnlyListing[])], amenitiesMap, listing => getListingProviderCode(listing) === 'iwg'), [allAminities, workspacesSelector, listingsSelector, amenitiesMap]);

  // ── Map accumulator ──────────────────────────────────────────────
  // Full map view shows pins for every listing/workspace the user has
  // discovered while exploring. Split view keeps paginated Redux items.
  const idOf = useCallback((item: ReadOnlyListing) => (isWorkspaceMode ? item.workspace?.id : item.id) as number | undefined, [isWorkspaceMode]);
  const [mapAccumulator, setMapAccumulator] = useState<Map<number, ReadOnlyListing>>(() => new Map());
  useEffect(() => {
    setMapAccumulator(new Map());
  }, [isWorkspaceMode]);
  const mergeIntoAccumulator = useCallback((incoming: ReadOnlyListing[]) => {
    if (!incoming || incoming.length === 0) return;
    setMapAccumulator(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const item of incoming) {
        const key = idOf(item);
        if (key == null) continue;
        if (next.get(key) === item) continue;
        next.set(key, item);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [idOf]);
  useEffect(() => {
    mergeIntoAccumulator(items);
  }, [items, mergeIntoAccumulator]);
  const mapItems = useMemo(() => Array.from(mapAccumulator.values()), [mapAccumulator]);
  const lastCountryCanvasFetch = useRef('');
  const lastSplitMapZoomRef = useRef<number | null>(null);
  useEffect(() => {
    lastSplitMapZoomRef.current = null;
    lastCountryCanvasFetch.current = '';
  }, [viewMode, isWorkspaceMode, geoLocation?.formatted_address]);
  const handleSplitMapViewportChanged = useCallback(async (center: {
    lat: number;
    lng: number;
  }, radiusKm: number, zoom: number) => {
    const prevZoom = lastSplitMapZoomRef.current;
    lastSplitMapZoomRef.current = zoom;
    if (prevZoom === null || prevZoom === zoom) return;
    if (isAreaWideGeo(geoLocation)) return;
    const fetchParams = {
      page: 1,
      limit: PAGE_SIZE,
      keepPreviousResults: true,
      searchLocation: {
        lat: center.lat,
        lng: center.lng,
        radius: Math.max(MIN_MAP_VIEWPORT_RADIUS_KM, Math.ceil(radiusKm))
      }
    };
    if (isWorkspaceMode) {
      dispatch(setWorkspacePage(1));
      dispatch(getWorkspaceData(fetchParams));
    } else {
      dispatch(setListingPage(1));
      dispatch(getListingData(fetchParams));
    }
  }, [dispatch, geoLocation, isWorkspaceMode]);
  const handleCanvasChanged = useCallback(async (center: {
    lat: number;
    lng: number;
  }, radiusKm: number) => {
    try {
      const apiFilters = getApiFilters(reduxFilters);
      const fetchType = isWorkspaceMode ? Carousel.workspace : Carousel.listing;
      const scope = geoAreaScope(geoLocation);
      if (scope) {
        const fetchKey = `${fetchType}|${geoAreaKey(scope)}|${JSON.stringify(apiFilters)}`;
        if (lastCountryCanvasFetch.current === fetchKey) return;
        lastCountryCanvasFetch.current = fetchKey;
      }
      const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
      const service = new ListingService(apiConfig);
      const viewport = {
        lat: center.lat,
        lng: center.lng,
        radius: Math.max(1, Math.ceil(radiusKm))
      };
      const params = scope ? service.getListingsDataParams(fetchType, 100, 0, viewport, apiFilters, undefined, undefined, undefined, scope.countryCode, scope.region) : service.getListingsDataParams(fetchType, 100, 0, viewport, apiFilters);
      const res = await service.getallListings(params);
      const fetched = (res?.results ?? []) as ReadOnlyListing[];
      mergeIntoAccumulator(fetched);
    } catch (err) {
      console.error('Canvas-driven map fetch failed', err);
    }
  }, [languageCode, isWorkspaceMode, reduxFilters, geoLocation, mergeIntoAccumulator]);

  // Once Redux has been hydrated, it's the source of truth — including a
  // genuine 0 from a fetch that returned no results. Falling back to the
  // SSR prop with `||` would mask that 0 and incorrectly show "Show more"
  // instead of the empty state when the user changes the search to a
  // location with no workspaces/listings.
  const apiTotal = hydrated ? isWorkspaceMode ? totalWorkspaceSel : totalListingSel : isWorkspaceMode ? totalWorkspace : totallisting;
  const total = apiTotal + (isWorkspaceMode ? 0 : iwgQuoteExtraCount);
  const primaryLoadingMore = isWorkspaceMode ? workspaceBtnLoading : listingBtnLoading;
  const primaryListRefreshing = isWorkspaceMode ? isWorkspaceLoading : isListingLoading;
  // Long-term also waits on the secondary IWG quote-only fetch before we can
  // call a search "empty" — otherwise the quote form flashes then cards land.
  const listRefreshing = primaryListRefreshing || awaitingIwgQuotes;
  const primaryAllLoaded = rawItems.length >= apiTotal;
  // Long-term: page the primary month/annual list first, then the IWG
  // Request Quote cards behind it.
  const pagingIwgQuotes = !isWorkspaceMode && isLongTermExplore && !awaitingIwgQuotes;
  const allLoaded = primaryAllLoaded && (!pagingIwgQuotes || iwgQuotesAllLoaded);
  const loadingMore = primaryLoadingMore || pagingIwgQuotes && iwgQuoteLoadingMore;
  const handleExploreLoadMore = () => {
    if (!primaryAllLoaded) handleLoadMore();else if (pagingIwgQuotes) void loadMoreIwgQuotes();
  };
  const effectiveViewMode = viewMode;

  // Compare mode: the results' type decides what a comparison holds.
  const compare = useExploreCompare(isWorkspaceMode ? CompareType.WORKSPACE : CompareType.LISTING, COMPARE_VIEW_NAME[effectiveViewMode] ?? 'grid');
  const showCompare = compare.enabledHere;
  const compareCardState = showCompare ? compare.cardState : undefined;

  // Long-term entry (month/annual filter): promo banner above the results
  // row. Zero results still show the inline quote form below it.
  // Dismiss is in-memory only — leaving and returning shows the banner again.
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [emptyQuoteDismissed, setEmptyQuoteDismissed] = useState(false);
  useEffect(() => {
    setEmptyQuoteDismissed(false);
    setPromoDismissed(false);
  }, [longTermFrequency, geoLocation?.formatted_address, geoLocation?.city]);

  // Only show the empty quote form once both primary + IWG quote fetches
  // have settled at zero — never while either is still pending.
  const showLongTermEmptyForm = isLongTermExplore && total === 0 && !listRefreshing && !emptyQuoteDismissed;

  // Promo banner with results only — empty long-term explore shows the form alone.
  // Don't wait on the IWG quote fetch here: if primary already has hits the CTA
  // can show; IWG-only hits land with listRefreshing already clear.
  const showLongTermPromoBanner = isLongTermExplore && !promoDismissed && total > 0 && !primaryListRefreshing;

  // Keep banner space reserved with a shimmer while long-term search settles,
  // so we don't flash the empty form CTA before results (or emptiness) land.
  // Skip when the real banner can already show (primary hits are enough).
  const showLongTermPromoBannerSkeleton = isLongTermExplore && !promoDismissed && listRefreshing && !showLongTermPromoBanner && !showLongTermEmptyForm;
  const longTermEmptyTarget = useMemo(() => ({
    spaceName: '',
    listingName: '',
    city: geoLocation?.city || geoLocation?.formatted_address?.split(',')[0]?.trim() || '',
    listingId: null,
    workspaceId: null,
    listingTypeId: null,
    source: 'banner' as const
  }), [geoLocation?.city, geoLocation?.formatted_address]);
  return <FilterProvider data-sentry-element="FilterProvider" data-sentry-component="ExploreClient" data-sentry-source-file="ExploreClient.tsx">
            <LongTermQuoteProvider data-sentry-element="LongTermQuoteProvider" data-sentry-source-file="ExploreClient.tsx">
            <ExploreFilterSubscriptionInit data-sentry-element="ExploreFilterSubscriptionInit" data-sentry-source-file="ExploreClient.tsx" />
            <ExploreFilterChips listingTypes={chipListingTypes} listingTypesLoading={chipListingTypesLoading} typeIn={selectedTypeIn as string} onOpenFilters={() => setFiltersOpen(true)} activeFilterCount={activeFilterCount} onSelectWorkspaces={handleSelectWorkspaces} onSelectType={handleSelectType} instantBooking={instantBookingActive} onToggleInstantBooking={handleToggleInstantBooking} data-sentry-element="ExploreFilterChips" data-sentry-source-file="ExploreClient.tsx" />

            <ExploreToolbar autocompleteInput={autocompleteInput as any} autocompleteContainer={autocompleteContainer as any} onSearch={handleSearch} onClearSearch={handleClearSearch} onDetectLocation={locationClick} viewMode={effectiveViewMode} onChangeView={handleChangeView} totalCount={total} listRefreshing={listRefreshing} isWorkspaceMode={isWorkspaceMode} searchDisplayKey={searchDisplayKey} geoLocationSource={geoLocationSource} onSelect={handlePlaceSelect} compareSlot={showCompare && <CompareToggleButton active={compare.active} onClick={() => compare.active ? compare.exit(CompareExitMethod.COMPARE_BUTTON) : compare.start()} />} data-sentry-element="ExploreToolbar" data-sentry-source-file="ExploreClient.tsx">
                {showLongTermPromoBannerSkeleton ? <LongTermLeasePromoBannerSkeleton /> : showLongTermPromoBanner && <LongTermLeasePromoBanner onDismiss={() => setPromoDismissed(true)} />}

                {!isLongTermExplore && <ExploreMembershipOfferBanner priceUnit={longTermFrequency} />}
            </ExploreToolbar>

            {showLongTermEmptyForm ? <LongTermBookingModal show variant="inline" target={longTermEmptyTarget} onClose={() => setEmptyQuoteDismissed(true)} /> : <>
                    {effectiveViewMode === Carousel.listing && <ExploreGrid items={items} total={total} allLoaded={allLoaded} loadingMore={loadingMore} listRefreshing={listRefreshing} onLoadMore={handleExploreLoadMore} isWorkspaceMode={isWorkspaceMode} compareCardState={compareCardState} />}

                    {effectiveViewMode === Carousel.split && <ExploreSplit items={items} total={total} allLoaded={allLoaded} loadingMore={loadingMore} listRefreshing={listRefreshing} onLoadMore={handleExploreLoadMore} isWorkspaceMode={isWorkspaceMode} onCanvasChanged={handleSplitMapViewportChanged} onToggleExpandMap={handleToggleExpandMap} compareCardState={compareCardState} />}

                    {effectiveViewMode === Carousel.fullMap && <ExploreMap items={mapItems} centroidSeed={items} isWorkspaceMode={isWorkspaceMode} onCanvasChanged={handleCanvasChanged} onToggleExpandMap={handleToggleExpandMap} />}
                </>}

            <ExploreFiltersModal open={filtersOpen} onClose={() => setFiltersOpen(false)} allAminities={filterAmenities} listingRes={listingRes} type={type} country={country} city={city} data-sentry-element="ExploreFiltersModal" data-sentry-source-file="ExploreClient.tsx" />

            {/* Stays visible in Map view too, where pins cannot be picked. */}
            {showCompare && compare.active && <CompareFloatingBar compare={compare} />}
            </LongTermQuoteProvider>
        </FilterProvider>;
};
export default ExploreClient;